<template>
  <!-- 领奖页面容器 -->
  <div class="claim-container">
    <!-- 背景装饰元素 -->
    <div class="background-decoration"></div>
    
    <!-- 主内容区域 -->
    <div class="content">
      <!-- 页面头部标题 -->
      <div class="page-header">
        <h1 class="main-title">🎉 恭喜中奖！</h1> <!-- 主标题 -->
        <p class="subtitle">请填写以下信息领取您的奖品</p> <!-- 副标题 -->
      </div>

      <!-- 已关注状态 - 显示领奖表单 -->
      <div v-if="isFollowing" class="form-card" :class="{ 'slide-in': formVisible }">
        <!-- 表单卡片头部 -->
        <div class="card-header">
          <div class="prize-badge">
            <span class="badge-icon">🏆</span> <!-- 奖品图标 -->
          </div>
          <h2 class="form-title">填写领奖信息</h2> <!-- 表单标题 -->
        </div>
        
        <!-- 领奖表单 -->
        <form class="claim-form" @submit.prevent="submitForm">
          <!-- 姓名字段 -->
          <div class="form-group">
            <label for="realName" class="label-float">
              <input 
                type="text" 
                id="realName" 
                v-model="formData.realName" 
                required 
                placeholder="请输入您的姓名"
                class="form-input"
                :class="{ 'input-focus': focusField === 'realName', 'input-filled': formData.realName }"
                @focus="focusField = 'realName'"
                @blur="focusField = ''"
              >
            </label>
          </div>
          
          <!-- 手机号字段 -->
          <div class="form-group">
            <label for="mobile" class="label-float">
              <input 
                type="tel" 
                id="mobile" 
                v-model="formData.mobile" 
                required 
                placeholder="请输入您的手机号码"
                class="form-input"
                :class="{ 'input-focus': focusField === 'mobile', 'input-filled': formData.mobile }"
                @focus="focusField = 'mobile'"
                @blur="focusField = ''"
              >
            </label>
          </div>
          
          <!-- 地址字段 -->
          <div class="form-group">
            <label for="userAddress" class="label-float textarea-label">
              <textarea 
                id="userAddress" 
                v-model="formData.userAddress" 
                required 
                placeholder="请输入详细收货地址"
                class="form-textarea"
                :class="{ 'input-focus': focusField === 'userAddress', 'input-filled': formData.userAddress }"
                @focus="focusField = 'userAddress'"
                @blur="focusField = ''"
              ></textarea>
            </label>
          </div>
          
          <!-- 提交按钮 -->
          <button 
            type="submit" 
            class="submit-btn"
            :disabled="isSubmitting"
          >
            <span v-if="!isSubmitting">提交领奖信息</span>
            <span v-else class="loading-spinner"></span>
          </button>
        </form>
      </div>

      <!-- 未关注状态 - 显示二维码 -->
      <div v-else class="follow-card" :class="{ 'slide-in': formVisible }">
        <!-- 关注卡片头部 -->
        <div class="card-header">
          <div class="prize-badge">
            <span class="badge-icon">📱</span> <!-- 手机图标 -->
          </div>
          <h2 class="follow-title">关注公众号领奖</h2> <!-- 关注标题 -->
        </div>
        
        <!-- 二维码容器 -->
        <div class="qrcode-container">
          <div class="qrcode-wrapper">
            <div class="qrcode-placeholder">
              <span class="qrcode-icon">🔍</span>
              <span class="qrcode-text">扫码关注</span>
            </div>
            <div class="qrcode-frame"></div>
          </div>
          <p class="qrcode-instruction">扫描上方二维码关注公众号</p>
        </div>
        
        <p class="follow-instruction">关注后即可领取您的奖品，获取更多精彩活动</p>
        
        <!-- 验证关注状态按钮 -->
        <button 
          class="verify-btn" 
          @click="verifyFollowStatus"
          :disabled="isVerifying"
        >
          <span v-if="!isVerifying">已关注，重新验证</span>
          <span v-else class="loading-spinner small-spinner"></span>
        </button>
      </div>
    </div>

    <!-- 成功提示弹窗 -->
    <div class="success-modal" v-if="showSuccess">
      <div class="modal-content">
        <div class="success-icon">✅</div> <!-- 成功图标 -->
        <h3 class="success-title">提交成功！</h3> <!-- 成功标题 -->
        <p class="success-message">您的领奖信息已成功提交，奖品将在3-5个工作日内发放</p> <!-- 成功提示信息 -->
        <button class="success-btn" @click="closeSuccess">返回首页</button> <!-- 返回按钮 -->
      </div>
    </div>
  </div>
</template>

<script>
// 导入API接口和全局存储
import { prizeAPI } from '../utils/api';
import globalStore from '../utils/globalStore.js';

/**
 * 奖品领取页面组件
 * 负责用户填写领奖信息和关注公众号验证
 */
export default {
  // 组件名称
  name: 'Prize',
  
  // 组件数据
  data() {
    return {
      isFollowing: false, // 用户关注状态
      formData: {
        realName: '', // 用户名
        mobile: '', // 手机号码
        userAddress: '', // 收货地址
        code: null
      },
      focusField: '', // 当前聚焦的表单字段
      formVisible: false, // 表单显示状态
      isSubmitting: false, // 表单提交状态
      isVerifying: false, // 关注验证状态
      showSuccess: false // 成功弹窗显示状态
    };
  },
  created() {
    // 在组件创建时获取URL参数并存储到全局状态
    const codeParam = { ...this.$route.query }
    this.formData.code = codeParam.code || null;
    //console.log('URL参数信息:', codeParam)
  },
  // 组件方法
  methods: {
    /**
     * 验证用户公众号关注状态
     * 调用API检查用户是否已关注公众号
     * 根据验证结果显示对应的界面
     */
    async verifyFollowStatus() {
      // 设置验证中状态
      this.isVerifying = true;
      
      try {
        // 调用API验证关注状态，包含全局URL参数
        const response = await prizeAPI.verifyFollowStatus(globalStore.getUrlParams());
        if (response.code==0) {
          // 更新关注状态
          this.isFollowing = response.data.isFollowing || true; // 默认设为true以确保表单显示
        } else {
          // 即使验证失败，也默认显示表单
          this.isFollowing = true;
          this.showToast('验证状态获取失败，已显示领奖表单');
        }
      } catch (error) {
        // 错误处理 - 为了测试方便，默认显示表单
        //console.error('验证关注状态失败:', error);
        this.isFollowing = true;
        this.showToast('网络异常，已显示领奖表单');
      } finally {
        // 无论成功失败，都设置表单可见并重置验证状态
        setTimeout(() => {
          this.formVisible = true;
        }, 100);
        this.isVerifying = false;
      }
    },

    /**
     * 提交领奖信息
     * 验证表单数据并提交到后端
     * 成功后显示成功弹窗
     */
    async submitForm() {
      // 简单验证 - 检查必填项
      //console.log(this.formData);

      if (!this.formData.realName || !this.formData.mobile || !this.formData.userAddress) {
        this.showToast('请填写完整的领奖信息');
        return;
      }

      // 验证手机号格式
      if (!/^1[3-9]\d{9}$/.test(this.formData.mobile)) {
        this.showToast('请输入有效的手机号码');
        return;
      }

      // 设置提交中状态
      this.isSubmitting = true;

      try {
        // 调用API提交表单数据，包含全局URL参数
        const response = await prizeAPI.submitPrizeInfo({...globalStore.getUrlParams(), ...this.formData});
        if (response.code==0) {
          // 显示成功弹窗
          this.showSuccess = true;
        } else {
          // 提交失败提示
          this.showToast('提交失败，请稍后重试');
        }
      } catch (error) {
        // 错误处理
        this.showToast(error.message || '提交失败，请稍后重试');
      } finally {
        // 无论成功失败，都重置提交状态
        this.isSubmitting = false;
      }
    },
    
    /**
     * 显示提示信息（Toast）
     * 创建并显示一个临时提示消息
     * @param {string} message - 要显示的消息内容
     */
    showToast(message) {
      // 创建toast元素
      const toast = document.createElement('div');
      toast.className = 'toast-message';
      toast.textContent = message;
      document.body.appendChild(toast);
      
      // 添加动画 - 延迟添加类以触发过渡动画
      setTimeout(() => {
        toast.classList.add('toast-show');
      }, 10);
      
      // 自动移除 - 2秒后淡出并移除元素
      setTimeout(() => {
        toast.classList.remove('toast-show');
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 300);
      }, 2000);
    },
    
    /**
     * 关闭成功弹窗并返回首页
     * 先隐藏弹窗，再跳转页面
     */
    closeSuccess() {
      // 隐藏成功弹窗
      this.showSuccess = false;
      // 延迟跳转，等待动画完成，传递全局URL参数
        setTimeout(() => {
          this.$router.push({
            path: '/',
            query: globalStore.getUrlParams()
          });
        }, 300);
    }
  },
  
  /**
   * 组件挂载后的初始化操作
   */
  mounted() {
    // 页面加载时自动检查关注状态
    this.verifyFollowStatus();
    
    // 为了确保表单显示，添加一个备用机制
    setTimeout(() => {
      if (!this.formVisible) {
        //console.log('备用机制：强制显示表单');
        this.isFollowing = true;
        this.formVisible = true;
      }
    }, 1000);
  }
};
</script>

<style scoped>
.claim-container {
  min-height: 100vh; /* 页面最小高度为视口高度 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); /* 紫色渐变背景 */
  padding: 20px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif; /* 设置中文字体 */
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 背景装饰 - 动态浮动元素 */
.background-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0; /* 置于底层 */
}

/* 使用伪元素创建装饰性圆形 */
.background-decoration::before,
.background-decoration::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  animation: float 15s ease-in-out infinite; /* 浮动动画 */
}

/* 右上装饰圆 */
.background-decoration::before {
  width: 600px;
  height: 600px;
  top: -300px;
  right: -200px;
  animation-delay: 0s;
}

/* 左下装饰圆 */
.background-decoration::after {
  width: 400px;
  height: 400px;
  bottom: -200px;
  left: -100px;
  animation-delay: -7s; /* 错开动画时间 */
}

/* 浮动动画定义 */
@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-30px) rotate(5deg); }
}

/* 内容容器 - 确保内容在背景之上 */
.content {
  position: relative;
  width: 100%;
  max-width: 500px;
  z-index: 1;
}

/* 页面头部样式 */
.page-header {
  text-align: center;
  margin-bottom: 30px;
}

/* 主标题样式 - 带有发光效果 */
.main-title {
  font-size: 2.5rem;
  color: white;
  margin-bottom: 10px;
  font-weight: 800;
  text-shadow: 0 4px 15px rgba(0,0,0,0.3);
  animation: glow 2s ease-in-out infinite alternate; /* 发光动画 */
}

/* 标题发光动画 */
@keyframes glow {
  from { text-shadow: 0 0 10px #fff, 0 0 20px #fff, 0 0 30px #667eea, 0 0 40px #667eea; }
  to { text-shadow: 0 0 20px #fff, 0 0 30px #764ba2, 0 0 40px #764ba2, 0 0 50px #764ba2; }
}

/* 副标题样式 */
.subtitle {
  font-size: 1.1rem;
  color: rgba(255,255,255,0.9);
  font-weight: 300;
}

/* 表单卡片基础样式 */
.form-card,
.follow-card {
  background: rgba(255,255,255,0.95); /* 半透明白色背景 */
  border-radius: 24px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1); /* 柔和阴影 */
  backdrop-filter: blur(10px); /* 背景模糊效果 */
  border: 1px solid rgba(255,255,255,0.2); /* 微妙边框 */
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.2, 0.8, 0.2, 1); /* 平滑过渡 */
}

/* 卡片滑入动画状态 */
.form-card.slide-in,
.follow-card.slide-in {
  opacity: 1;
  transform: translateY(0);
}

/* 卡片头部样式 */
.card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px;
  position: relative;
}

/* 奖品徽章样式 - 突出显示在卡片上方 */
.prize-badge {
  position: absolute;
  top: -40px; /* 超出卡片上方 */
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #ff6b6b, #ffd93d); /* 橙色到黄色渐变 */
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  border: 3px solid white; /* 白色边框 */
}

/* 徽章图标样式 */
.badge-icon {
  font-size: 2.5rem;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1)); /* 图标阴影 */
}

/* 表单标题样式 */
.form-title,
.follow-title {
  font-size: 1.8rem;
  color: #333;
  margin-top: 20px;
  font-weight: 700;
}

/* 表单容器 */
.claim-form {
  width: 100%;
}

/* 表单组样式 */
.form-group {
  margin-bottom: 25px;
  position: relative;
}

/* 浮动标签容器 */
.label-float {
  position: relative;
  display: block;
}

/* 标签文本样式 */
.label-text {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  padding: 0 5px;
  color: #666;
  font-size: 14px;
  transition: all 0.3s ease; /* 标签动画效果 */
  pointer-events: none;
  z-index: 1;
}

/* 文本域标签位置调整 */
.textarea-label .label-text {
  top: 20px;
}

/* 输入框基础样式 */
.form-input,
.form-textarea {
  width: 100%;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s ease; /* 输入框过渡效果 */
  background: transparent;
  color: #333;
}

/* 文本域特殊样式 */
.form-textarea {
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
}

/* 输入框聚焦状态 */
.form-input:focus,
.form-textarea:focus,
.form-input.input-focus,
.form-textarea.input-focus {
  outline: none;
  border-color: #667eea; /* 聚焦时改变边框颜色 */
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1); /* 聚焦光晕效果 */
}

/* 标签浮动动画效果 */
.form-input.input-filled + .label-text,
.form-textarea.input-filled + .label-text,
.form-input:focus + .label-text,
.form-textarea:focus + .label-text,
.form-input.input-focus + .label-text,
.form-textarea.input-focus + .label-text {
  top: -10px; /* 标签上浮 */
  font-size: 12px; /* 标签缩小 */
  color: #667eea; /* 标签变色 */
  background: white; /* 背景色避免与文字重叠 */
}

/* 文本域标签浮动效果 */
.textarea-label .form-input.input-filled + .label-text,
.textarea-label .form-textarea.input-filled + .label-text,
.textarea-label .form-input:focus + .label-text,
.textarea-label .form-textarea:focus + .label-text,
.textarea-label .form-input.input-focus + .label-text,
.textarea-label .form-textarea.input-focus + .label-text {
  top: -10px;
}

/* 提交按钮样式 */
.submit-btn {
  width: 100%;
  padding: 18px 0;
  background: linear-gradient(135deg, #4caf50 0%, #8bc34a 100%); /* 绿色渐变背景 */
  color: white;
  border: none;
  border-radius: 50px; /* 圆角按钮 */
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease; /* 过渡效果 */
  position: relative;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(76, 175, 80, 0.3); /* 按钮阴影 */
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* 按钮悬停效果 */
.submit-btn:hover:not(:disabled) {
  transform: translateY(-3px); /* 按钮上移 */
  box-shadow: 0 8px 25px rgba(76, 175, 80, 0.4); /* 增强阴影 */
  background: linear-gradient(135deg, #8bc34a 0%, #4caf50 100%); /* 反向渐变 */
}

/* 按钮禁用状态 */
.submit-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 二维码容器 */
.qrcode-container {
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 二维码容器包装 */
.qrcode-wrapper {
  position: relative;
  width: 240px;
  height: 240px;
  margin-bottom: 20px;
}

/* 二维码占位图样式 */
.qrcode-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

/* 二维码图标样式 */
.qrcode-icon {
  font-size: 3rem;
  margin-bottom: 10px;
  opacity: 0.7;
}

/* 二维码文本说明 */
.qrcode-text {
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

/* 二维码虚线边框装饰 */
.qrcode-frame {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  bottom: 10px;
  border: 2px dashed #ccc;
  border-radius: 10px;
  z-index: 1;
}

/* 二维码扫描说明 */
.qrcode-instruction {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
  font-weight: 500;
}

/* 关注公众号说明文本 */
.follow-instruction {
  color: #666;
  margin-bottom: 30px;
  line-height: 1.5;
  text-align: center;
  font-size: 14px;
}

/* 验证按钮样式 */
.verify-btn {
  width: 100%;
  padding: 18px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); /* 紫色渐变 */
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
  font-weight: 500;
}

/* 验证按钮悬停效果 */
.verify-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%); /* 反向渐变 */
}

/* 验证按钮禁用状态 */
.verify-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 加载动画样式 */
.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3); /* 半透明边框 */
  border-radius: 50%;
  border-top-color: white; /* 顶部亮色边框 */
  animation: spin 1s ease-in-out infinite; /* 旋转动画 */
}

/* 小型加载动画 */
.small-spinner {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

/* 旋转动画定义 */
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 成功弹窗背景 */
.success-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000; /* 最高层级 */
  backdrop-filter: blur(5px); /* 背景模糊效果 */
  animation: fadeIn 0.3s ease-out;
}

/* 淡入动画 */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 弹窗内容 */
.modal-content {
  background: white;
  border-radius: 24px;
  padding: 40px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.5s cubic-bezier(0.2, 0.8, 0.2, 1); /* 弹性滑入 */
}

/* 弹窗滑入动画 */
@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 成功图标 */
.success-icon {
  font-size: 4rem;
  margin-bottom: 20px;
  animation: bounce 2s infinite; /* 弹跳动画 */
}

/* 弹跳动画 */
@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-20px); }
  60% { transform: translateY(-10px); }
}

/* 成功标题 */
.success-title {
  font-size: 1.8rem;
  color: #4caf50; /* 绿色表示成功 */
  margin-bottom: 15px;
  font-weight: 700;
}

/* 成功消息文本 */
.success-message {
  color: #666;
  margin-bottom: 30px;
  line-height: 1.6;
  font-size: 14px;
}

/* 成功确认按钮 */
.success-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 15px 40px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

/* 成功按钮悬停效果 */
.success-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* 全局Toast提示样式 - 使用:global避免scoped隔离 */
:global(.toast-message) {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  background: rgba(0, 0, 0, 0.8); /* 半透明黑色背景 */
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 2000; /* 高于弹窗层级 */
  opacity: 0;
  transition: all 0.3s ease;
  pointer-events: none; /* 不影响其他元素点击 */
}

/* Toast显示状态 */
:global(.toast-message.toast-show) {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/* 响应式设计 - 移动设备适配 */
@media (max-width: 480px) {
  /* 容器内边距调整 */
  .claim-container {
    padding: 15px;
  }
  
  /* 标题字体调整 */
  .main-title {
    font-size: 2rem;
  }
  
  /* 卡片内边距调整 */
  .form-card,
  .follow-card {
    padding: 25px 20px;
  }
  
  /* 徽章尺寸缩小 */
  .prize-badge {
    width: 60px;
    height: 60px;
    top: -35px;
  }
  
  /* 徽章图标缩小 */
  .badge-icon {
    font-size: 2rem;
  }
  
  /* 表单标题缩小 */
  .form-title,
  .follow-title {
    font-size: 1.5rem;
  }
  
  /* 二维码尺寸缩小 */
  .qrcode-wrapper {
    width: 200px;
    height: 200px;
  }
  
  /* 二维码图标缩小 */
  .qrcode-icon {
    font-size: 2.5rem;
  }
  
  /* 弹窗内容内边距调整 */
  .modal-content {
    padding: 30px 20px;
  }
  
  /* 成功图标缩小 */
  .success-icon {
    font-size: 3rem;
  }
  
  /* 成功标题缩小 */
  .success-title {
    font-size: 1.6rem;
  }
}
</style>